"use client";

import { SectionCard } from "@repo/dashboard-core";
import {
  getTrustpilotReviewCollector,
  type TrustpilotCollectorPayload,
} from "@repo/dashboard-core/actions";
import { trackReview } from "@repo/dashboard-core/utils";
import Image from "next/image";
import Script from "next/script";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { useBrand } from "@/lib/context/BrandContext";

// `import("react")` rather than bare names: the augmented interface is
// exported, so declaration emit needs names it can reach (TS4033).
declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-namespace -- JSX augmentation
  namespace JSX {
    interface IntrinsicElements {
      "trustbox-verified-review-collector": import("react").HTMLAttributes<HTMLElement> & {
        ref?: import("react").Ref<HTMLElement>;
        "template-id": string;
        "businessunit-id": string;
        locale: string;
        "style-size"?: string;
        "style-width"?: string;
        "border-type"?: string;
        background?: string;
        "has-animated-stars"?: string;
        "host-bypass"?: string;
      };
    }
  }
}

/** Trustpilot's In-app review collector template (the same for every business). */
const COLLECTOR_TEMPLATE_ID = "53ch7c1d9be8a02f64d31e79";

/** The collector's languages we also speak; pt, tr and el fall back to English. */
const COLLECTOR_LOCALES: Record<string, string> = {
  en: "en-GB",
  fr: "fr-FR",
  es: "es-ES",
  it: "it-IT",
  de: "de-DE",
};

type Collector = {
  businessUnitId: string;
  payload: TrustpilotCollectorPayload;
};

const askedKey = (applicationId: string) =>
  `trustpilot-review-asked:${applicationId}`;

// Storage can throw (private mode, blocked site data); treat that as not asked.
function wasAsked(applicationId: string): boolean {
  try {
    return localStorage.getItem(askedKey(applicationId)) === "true";
  } catch {
    return false;
  }
}

function markAsked(applicationId: string) {
  try {
    localStorage.setItem(askedKey(applicationId), "true");
  } catch {
    // ponytail: per browser; a server-side flag if members switch devices mid-trial
  }
}

/**
 * GD-125 — asks for a Trustpilot review through Trustpilot's In-app review
 * collector: picking a star opens Trustpilot's verified review form in a
 * pop-up, with the member and the rating already filled in. Renders nothing
 * for a brand with no `trustpilotCollectorBusinessUnitId` (its Trustpilot
 * plan has no collector).
 *
 * The review goes to the brand that sold the purchase, whose business the API
 * answers with, even on another brand's member zone: a Prepaid Mobile
 * Recharge order paid through Top Up Mobile's merchant lands its member on
 * member.topup-mobile.co.
 *
 * Trustpilot's widget draws its stars only on a host that matches the
 * business's domains on Trustpilot. The card sets `host-bypass`, which skips
 * that check, for another brand's business on this brand's member host, and
 * for its own on a member host the brand lists in
 * `trustpilotCollectorBypassHosts`; Trustpilot still checks the payload's
 * signature. Away from the member host (localhost, previews) another brand's
 * business renders nothing: Trustpilot would show it without its stars.
 *
 * `onPicked` fires once a star was picked for the purchase, on this visit or
 * an earlier one. A card that only shows doesn't count: the page's feedback
 * modal still asks about that purchase (GD-144).
 */
export function TrustpilotReviewCard({
  applicationId,
  onPicked,
}: {
  applicationId: string;
  onPicked?: (applicationId: string) => void;
}) {
  const t = useTranslations("dashboard");
  const locale = useLocale();
  const brand = useBrand();
  const widgetRef = useRef<HTMLElement>(null);
  const [collector, setCollector] = useState<Collector | null>(null);

  useEffect(() => {
    const expectedBusinessUnitId = brand.trustpilotCollectorBusinessUnitId;
    if (!expectedBusinessUnitId) return;

    if (wasAsked(applicationId)) {
      onPicked?.(applicationId);
      return;
    }
    let cancelled = false;

    getTrustpilotReviewCollector(applicationId, brand.domain).then(
      ({ success, businessUnitId, payload }) => {
        if (cancelled || !success || !businessUnitId || !payload) return;
        // A purchase made on another brand comes back with that brand's
        // business, whose domains on Trustpilot this host doesn't match: only
        // the member host skips that check for it.
        if (
          businessUnitId !== expectedBusinessUnitId &&
          window.location.hostname !== `member.${brand.domain}`
        )
          return;
        setCollector({ businessUnitId, payload });
      },
    );

    return () => {
      cancelled = true;
    };
    // Strings only: each server action refreshes the page's server data, which
    // hands the card a new brand object, and a list here would ask again on
    // every one.
  }, [
    applicationId,
    brand.domain,
    brand.trustpilotCollectorBusinessUnitId,
    onPicked,
  ]);

  // Trustpilot's handshake: once the widget is ready it waits for the
  // customer as a `customerDataResponse` event.
  useEffect(() => {
    if (!collector) return;
    let cancelled = false;

    customElements
      .whenDefined("trustbox-verified-review-collector")
      .then(() => {
        const widget = widgetRef.current as
          | (HTMLElement & { onReady?: () => void })
          | null;
        if (cancelled || !widget) return;

        widget.onReady = () => {
          widget.dispatchEvent(
            new CustomEvent("customerDataResponse", {
              detail: collector.payload,
            }),
          );
          // GD-141 — the widget's steps count as Travel's do (TD-167).
          trackReview(applicationId, { surface: "widget", step: "shown" });
        };
      });

    return () => {
      cancelled = true;
    };
  }, [applicationId, collector]);

  if (!collector) return null;

  // Past the line above only in the browser: the collector is set in an effect.
  const hostBypass =
    collector.businessUnitId !== brand.trustpilotCollectorBusinessUnitId ||
    brand.trustpilotCollectorBypassHosts?.includes(window.location.hostname);

  return (
    <SectionCard>
      <Script
        id="trustpilot-review-collector"
        src="https://reviewcollection.trustpilot.com/verifiedreviewcollector/main.js"
        strategy="afterInteractive"
      />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-6">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#00b67a]/10">
            <Image
              src="/img/trustpilot-star.svg"
              alt=""
              width={40}
              height={40}
              unoptimized
            />
          </div>
          <div className="min-w-0">
            <h3 className="text-xl font-bold text-text-primary">
              {t("review-card.title")}
            </h3>
            <p className="mt-1 text-sm text-text-secondary">
              {t("review-card.description")}
            </p>
          </div>
        </div>
        {/* The widget sends no "review submitted" event. Its whole box is one
            link whose click handler calls preventDefault and opens the review
            popup, so a prevented click means the popup opened (TD-150). From
            the next visit the card stays away. */}
        <div className="flex w-full flex-col gap-3 sm:w-[272px] sm:shrink-0">
          {/* The widget pads its fixed 124px box by 24px on every side, white
              on our white card; the top margin takes that back. The bottom
              padding stays: the caption must sit outside the box, which is all
              link, and a two-line "Review us on" (fr, de, es, it) pushes the
              stars 6px past it. */}
          <div
            className="-mt-4 sm:-mt-6"
            onClick={(event) => {
              if (!event.nativeEvent.defaultPrevented) return;
              markAsked(applicationId);
              onPicked?.(applicationId);
              trackReview(applicationId, {
                surface: "widget",
                step: "pressed",
              });
            }}
          >
            <trustbox-verified-review-collector
              ref={widgetRef}
              className="trustpilot-widget"
              template-id={COLLECTOR_TEMPLATE_ID}
              businessunit-id={collector.businessUnitId}
              locale={COLLECTOR_LOCALES[locale] ?? "en-GB"}
              style-size="M"
              style-width="100%"
              border-type="none"
              background="none"
              has-animated-stars="true"
              // Read once, when the widget connects.
              host-bypass={hostBypass ? "true" : undefined}
            />
          </div>
          {/* Centred, not right-aligned as in the design: the widget centres
              its stars inside its own padding, so this lines up with them. */}
          <p className="text-center text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-text-muted">
            {t("review-card.caption")}
          </p>
        </div>
      </div>
    </SectionCard>
  );
}
